import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export const UserAuthContext = createContext();

export const UserAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch('https://valleyapi-dr9y.onrender.com/api/refresh-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to refresh token');
      }

      localStorage.setItem('user_token', data.token);
      if (data.refreshToken) {
        localStorage.setItem('refresh_token', data.refreshToken);
      }
      const storedUser = localStorage.getItem('user_user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
      return true;
    } catch (err) {
      return false;
    }
  };

  const checkTokenValidity = async () => {
    const token = localStorage.getItem('user_token');
    const storedUser = localStorage.getItem('user_user');
    if (!token || !storedUser) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await fetch('https://valleyapi-dr9y.onrender.com/api/verify-token', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.valid) {
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          setUser(null);
          setLoading(false);
        } else {
          setUser(JSON.parse(storedUser));
          setLoading(false);
        }
      } else {
        setUser(JSON.parse(storedUser));
        setLoading(false);
      }
    } catch (err) {
      console.error('❌ Error checking token:', err.message);
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        setUser(null);
        setLoading(false);
      } else {
        setUser(JSON.parse(storedUser));
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('user_token');
      const storedUser = localStorage.getItem('user_user');
      if (token && storedUser) {
        try {
          await checkTokenValidity();
        } catch (err) {
          console.error('❌ Error parsing stored user:', err);
          setUser(null);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    initializeAuth();

    const interval = setInterval(checkTokenValidity, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [navigate]);

  const login = (userData, token, refreshToken) => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_user');
    localStorage.setItem('user_token', token);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
    localStorage.setItem('user_user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      fetch('https://valleyapi-dr9y.onrender.com/api/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        keepalive: true,
      }).catch(() => {});
    }
    localStorage.removeItem('user_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_user');
    setUser(null);
    navigate('/', { replace: true });
  };

  return (
    <UserAuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </UserAuthContext.Provider>
  );
};
